/**
 * Metrics Service
 * Comprehensive application metrics collection, aggregation, and export
 *
 * Features:
 * - Multiple metric types (counter, gauge, histogram, summary, timer)
 * - Dimensional tagging with label support
 * - Aggregation (sum, avg, min, max, percentiles)
 * - Multiple export formats (Prometheus, StatsD, JSON, OpenTelemetry)
 * - Real-time streaming and batch export
 * - Memory-efficient circular buffers
 * - Custom dashboards and alerts
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Metric types
 */
export enum MetricType {
  COUNTER = 'counter',       // Monotonically increasing value
  GAUGE = 'gauge',           // Point-in-time value
  HISTOGRAM = 'histogram',   // Distribution of values
  SUMMARY = 'summary',       // Pre-calculated percentiles
  TIMER = 'timer'            // Duration measurements
}

/**
 * Aggregation types
 */
export enum AggregationType {
  SUM = 'sum',
  COUNT = 'count',
  AVG = 'avg',
  MIN = 'min',
  MAX = 'max',
  LAST = 'last',
  RATE = 'rate',
  P50 = 'p50',
  P75 = 'p75',
  P90 = 'p90',
  P95 = 'p95',
  P99 = 'p99'
}

/**
 * Export formats
 */
export enum ExportFormat {
  PROMETHEUS = 'prometheus',
  STATSD = 'statsd',
  JSON = 'json',
  OPENTELEMETRY = 'opentelemetry',
  GRAPHITE = 'graphite',
  INFLUXDB = 'influxdb'
}

/**
 * Metric unit types
 */
export enum MetricUnit {
  NONE = '',
  BYTES = 'bytes',
  KILOBYTES = 'kilobytes',
  MEGABYTES = 'megabytes',
  MILLISECONDS = 'milliseconds',
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  PERCENT = 'percent',
  COUNT = 'count',
  REQUESTS = 'requests',
  ERRORS = 'errors',
  OPERATIONS = 'operations',
  TOKENS = 'tokens',
  QUERIES = 'queries'
}

/**
 * Alert severity
 */
export enum AlertSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

/**
 * Alert condition operators
 */
export enum AlertOperator {
  GREATER_THAN = 'gt',
  LESS_THAN = 'lt',
  EQUAL = 'eq',
  NOT_EQUAL = 'ne',
  GREATER_OR_EQUAL = 'gte',
  LESS_OR_EQUAL = 'lte'
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Metric data point
 */
export interface MetricPoint {
  value: number
  timestamp: number
  labels?: Record<string, string>
}

/**
 * Metric definition
 */
export interface MetricDefinition {
  name: string
  type: MetricType
  description?: string
  unit?: MetricUnit
  labels?: string[]
  buckets?: number[] // For histograms
}

/**
 * Metric metadata
 */
export interface MetricMetadata {
  name: string
  type: MetricType
  description: string
  unit: MetricUnit
  labels: string[]
  createdAt: number
  lastUpdated: number
  pointCount: number
}

/**
 * Histogram bucket
 */
export interface HistogramBucket {
  le: number // Less than or equal
  count: number
}

/**
 * Histogram data
 */
export interface HistogramData {
  buckets: HistogramBucket[]
  sum: number
  count: number
  min: number
  max: number
}

/**
 * Summary data
 */
export interface SummaryData {
  count: number
  sum: number
  min: number
  max: number
  avg: number
  quantiles: Map<number, number> // quantile -> value
}

/**
 * Timer result
 */
export interface TimerResult<T> {
  result: T
  duration: number
  success: boolean
}

/**
 * Aggregated metric
 */
export interface AggregatedMetric {
  name: string
  type: MetricType
  value: number
  aggregation: AggregationType
  labels: Record<string, string>
  timestamp: number
  window?: number // Aggregation window in ms
}

/**
 * Metric query
 */
export interface MetricQuery {
  name: string | RegExp
  labels?: Record<string, string>
  startTime?: number
  endTime?: number
  aggregation?: AggregationType
  groupBy?: string[]
  limit?: number
}

/**
 * Query result
 */
export interface QueryResult {
  metrics: AggregatedMetric[]
  total: number
  executionTime: number
}

/**
 * Metric alert definition
 */
export interface MetricAlert {
  id: string
  name: string
  description?: string
  metric: string
  condition: AlertCondition
  severity: AlertSeverity
  labels?: Record<string, string>
  cooldownMs: number
  enabled: boolean
  lastTriggered?: number
  triggerCount: number
}

/**
 * Alert condition
 */
export interface AlertCondition {
  operator: AlertOperator
  threshold: number
  aggregation: AggregationType
  windowMs: number
}

/**
 * Alert event
 */
export interface AlertEvent {
  alertId: string
  alertName: string
  severity: AlertSeverity
  metric: string
  value: number
  threshold: number
  operator: AlertOperator
  timestamp: number
  labels?: Record<string, string>
}

/**
 * Export configuration
 */
export interface ExportConfig {
  format: ExportFormat
  endpoint?: string
  interval?: number
  batchSize?: number
  headers?: Record<string, string>
  prefix?: string
  defaultLabels?: Record<string, string>
}

/**
 * Metrics service configuration
 */
export interface MetricsConfig {
  // Collection settings
  enabled: boolean
  maxMetrics: number
  maxPointsPerMetric: number
  defaultLabels: Record<string, string>

  // Flush settings
  flushIntervalMs: number
  batchSize: number

  // Histogram defaults
  defaultBuckets: number[]

  // Summary defaults
  defaultQuantiles: number[]

  // Export settings
  exports: ExportConfig[]

  // Alert settings
  alertCheckIntervalMs: number
}

/**
 * Metrics service stats
 */
export interface MetricsStats {
  totalMetrics: number
  totalPoints: number
  metricsPerType: Record<MetricType, number>
  exportStats: ExportStats[]
  alertStats: {
    total: number
    enabled: number
    triggered: number
  }
  memoryUsageBytes: number
  uptime: number
}

/**
 * Export stats
 */
export interface ExportStats {
  format: ExportFormat
  endpoint?: string
  successCount: number
  errorCount: number
  lastExport?: number
  lastError?: string
}

/**
 * Metric event listener
 */
export type MetricEventListener = (event: MetricEvent) => void

/**
 * Metric event
 */
export interface MetricEvent {
  type: 'record' | 'flush' | 'alert' | 'export' | 'error'
  metric?: string
  value?: number
  labels?: Record<string, string>
  error?: string
  timestamp: number
}

// ============================================================================
// DEFAULT CONFIGURATION
// ============================================================================

const DEFAULT_CONFIG: MetricsConfig = {
  enabled: true,
  maxMetrics: 10000,
  maxPointsPerMetric: 1000,
  defaultLabels: {
    service: 'linkbox-web',
    env: process.env.NODE_ENV || 'development'
  },
  flushIntervalMs: 10000,
  batchSize: 100,
  defaultBuckets: [0.005, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5, 10],
  defaultQuantiles: [0.5, 0.75, 0.9, 0.95, 0.99],
  exports: [],
  alertCheckIntervalMs: 60000
}

// ============================================================================
// CIRCULAR BUFFER
// ============================================================================

class CircularBuffer<T> {
  private buffer: T[]
  private head: number = 0
  private tail: number = 0
  private count: number = 0
  private maxSize: number

  constructor(maxSize: number) {
    this.maxSize = maxSize
    this.buffer = new Array(maxSize)
  }

  push(item: T): void {
    this.buffer[this.tail] = item
    this.tail = (this.tail + 1) % this.maxSize

    if (this.count < this.maxSize) {
      this.count++
    } else {
      this.head = (this.head + 1) % this.maxSize
    }
  }

  getAll(): T[] {
    if (this.count === 0) return []

    const result: T[] = []
    let idx = this.head
    for (let i = 0; i < this.count; i++) {
      result.push(this.buffer[idx])
      idx = (idx + 1) % this.maxSize
    }
    return result
  }

  clear(): void {
    this.head = 0
    this.tail = 0
    this.count = 0
  }

  size(): number {
    return this.count
  }
}

// ============================================================================
// METRIC CLASSES
// ============================================================================

/**
 * Base metric class
 */
abstract class BaseMetric {
  protected name: string
  protected type: MetricType
  protected description: string
  protected unit: MetricUnit
  protected labelNames: string[]
  protected createdAt: number
  protected lastUpdated: number

  constructor(
    name: string,
    type: MetricType,
    description: string = '',
    unit: MetricUnit = MetricUnit.NONE,
    labelNames: string[] = []
  ) {
    this.name = name
    this.type = type
    this.description = description
    this.unit = unit
    this.labelNames = labelNames
    this.createdAt = Date.now()
    this.lastUpdated = Date.now()
  }

  getName(): string {
    return this.name
  }

  getType(): MetricType {
    return this.type
  }

  getMetadata(): MetricMetadata {
    return {
      name: this.name,
      type: this.type,
      description: this.description,
      unit: this.unit,
      labels: this.labelNames,
      createdAt: this.createdAt,
      lastUpdated: this.lastUpdated,
      pointCount: this.getPointCount()
    }
  }

  abstract getPointCount(): number
  abstract getValue(labels?: Record<string, string>): number
  abstract reset(): void
}

/**
 * Counter metric - monotonically increasing
 */
class Counter extends BaseMetric {
  private values: Map<string, number> = new Map()

  constructor(
    name: string,
    description: string = '',
    unit: MetricUnit = MetricUnit.COUNT,
    labelNames: string[] = []
  ) {
    super(name, MetricType.COUNTER, description, unit, labelNames)
  }

  inc(labels?: Record<string, string>, value: number = 1): void {
    const key = this.labelsToKey(labels)
    const current = this.values.get(key) || 0
    this.values.set(key, current + value)
    this.lastUpdated = Date.now()
  }

  getValue(labels?: Record<string, string>): number {
    return this.values.get(this.labelsToKey(labels)) || 0
  }

  getPointCount(): number {
    return this.values.size
  }

  reset(): void {
    this.values.clear()
  }

  getAllValues(): Map<string, number> {
    return new Map(this.values)
  }

  private labelsToKey(labels?: Record<string, string>): string {
    if (!labels || Object.keys(labels).length === 0) return '__default__'
    return Object.entries(labels)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join(',')
  }
}

/**
 * Gauge metric - point-in-time value
 */
class Gauge extends BaseMetric {
  private values: Map<string, number> = new Map()

  constructor(
    name: string,
    description: string = '',
    unit: MetricUnit = MetricUnit.NONE,
    labelNames: string[] = []
  ) {
    super(name, MetricType.GAUGE, description, unit, labelNames)
  }

  set(value: number, labels?: Record<string, string>): void {
    const key = this.labelsToKey(labels)
    this.values.set(key, value)
    this.lastUpdated = Date.now()
  }

  inc(labels?: Record<string, string>, value: number = 1): void {
    const key = this.labelsToKey(labels)
    const current = this.values.get(key) || 0
    this.values.set(key, current + value)
    this.lastUpdated = Date.now()
  }

  dec(labels?: Record<string, string>, value: number = 1): void {
    this.inc(labels, -value)
  }

  getValue(labels?: Record<string, string>): number {
    return this.values.get(this.labelsToKey(labels)) || 0
  }

  getPointCount(): number {
    return this.values.size
  }

  reset(): void {
    this.values.clear()
  }

  getAllValues(): Map<string, number> {
    return new Map(this.values)
  }

  private labelsToKey(labels?: Record<string, string>): string {
    if (!labels || Object.keys(labels).length === 0) return '__default__'
    return Object.entries(labels)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join(',')
  }
}

/**
 * Histogram metric - distribution of values
 */
class Histogram extends BaseMetric {
  private buckets: number[]
  private bucketCounts: Map<string, number[]> = new Map()
  private sums: Map<string, number> = new Map()
  private counts: Map<string, number> = new Map()
  private mins: Map<string, number> = new Map()
  private maxs: Map<string, number> = new Map()

  constructor(
    name: string,
    description: string = '',
    unit: MetricUnit = MetricUnit.NONE,
    labelNames: string[] = [],
    buckets: number[] = DEFAULT_CONFIG.defaultBuckets
  ) {
    super(name, MetricType.HISTOGRAM, description, unit, labelNames)
    this.buckets = [...buckets].sort((a, b) => a - b)
  }

  observe(value: number, labels?: Record<string, string>): void {
    const key = this.labelsToKey(labels)

    // Initialize if needed
    if (!this.bucketCounts.has(key)) {
      this.bucketCounts.set(key, new Array(this.buckets.length).fill(0))
      this.sums.set(key, 0)
      this.counts.set(key, 0)
      this.mins.set(key, Infinity)
      this.maxs.set(key, -Infinity)
    }

    // Update bucket counts
    const bucketCountsArr = this.bucketCounts.get(key)!
    for (let i = 0; i < this.buckets.length; i++) {
      if (value <= this.buckets[i]) {
        bucketCountsArr[i]++
      }
    }

    // Update aggregates
    this.sums.set(key, this.sums.get(key)! + value)
    this.counts.set(key, this.counts.get(key)! + 1)
    this.mins.set(key, Math.min(this.mins.get(key)!, value))
    this.maxs.set(key, Math.max(this.maxs.get(key)!, value))
    this.lastUpdated = Date.now()
  }

  getValue(labels?: Record<string, string>): number {
    const key = this.labelsToKey(labels)
    const count = this.counts.get(key) || 0
    const sum = this.sums.get(key) || 0
    return count > 0 ? sum / count : 0 // Return average
  }

  getData(labels?: Record<string, string>): HistogramData {
    const key = this.labelsToKey(labels)
    const bucketCountsArr = this.bucketCounts.get(key) || new Array(this.buckets.length).fill(0)

    return {
      buckets: this.buckets.map((le, i) => ({ le, count: bucketCountsArr[i] })),
      sum: this.sums.get(key) || 0,
      count: this.counts.get(key) || 0,
      min: this.mins.get(key) === Infinity ? 0 : (this.mins.get(key) || 0),
      max: this.maxs.get(key) === -Infinity ? 0 : (this.maxs.get(key) || 0)
    }
  }

  getPercentile(p: number, labels?: Record<string, string>): number {
    const data = this.getData(labels)
    if (data.count === 0) return 0

    const target = data.count * p
    for (let i = 0; i < data.buckets.length; i++) {
      if (data.buckets[i].count >= target) {
        if (i === 0) return data.buckets[0].le / 2
        // Linear interpolation
        const prevCount = i > 0 ? data.buckets[i - 1].count : 0
        const prevLe = i > 0 ? data.buckets[i - 1].le : 0
        const fraction = (target - prevCount) / (data.buckets[i].count - prevCount)
        return prevLe + fraction * (data.buckets[i].le - prevLe)
      }
    }
    return data.max
  }

  getPointCount(): number {
    let total = 0
    for (const count of this.counts.values()) {
      total += count
    }
    return total
  }

  reset(): void {
    this.bucketCounts.clear()
    this.sums.clear()
    this.counts.clear()
    this.mins.clear()
    this.maxs.clear()
  }

  private labelsToKey(labels?: Record<string, string>): string {
    if (!labels || Object.keys(labels).length === 0) return '__default__'
    return Object.entries(labels)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join(',')
  }
}

/**
 * Summary metric - pre-calculated quantiles
 */
class Summary extends BaseMetric {
  private quantiles: number[]
  private values: Map<string, CircularBuffer<number>> = new Map()
  private maxAge: number
  private maxSize: number

  constructor(
    name: string,
    description: string = '',
    unit: MetricUnit = MetricUnit.NONE,
    labelNames: string[] = [],
    quantiles: number[] = DEFAULT_CONFIG.defaultQuantiles,
    maxSize: number = 1000
  ) {
    super(name, MetricType.SUMMARY, description, unit, labelNames)
    this.quantiles = quantiles
    this.maxAge = 600000 // 10 minutes
    this.maxSize = maxSize
  }

  observe(value: number, labels?: Record<string, string>): void {
    const key = this.labelsToKey(labels)

    if (!this.values.has(key)) {
      this.values.set(key, new CircularBuffer<number>(this.maxSize))
    }

    this.values.get(key)!.push(value)
    this.lastUpdated = Date.now()
  }

  getValue(labels?: Record<string, string>): number {
    const data = this.getData(labels)
    return data.avg
  }

  getData(labels?: Record<string, string>): SummaryData {
    const key = this.labelsToKey(labels)
    const buffer = this.values.get(key)

    if (!buffer || buffer.size() === 0) {
      return {
        count: 0,
        sum: 0,
        min: 0,
        max: 0,
        avg: 0,
        quantiles: new Map()
      }
    }

    const values = buffer.getAll()
    const sorted = [...values].sort((a, b) => a - b)

    const count = values.length
    const sum = values.reduce((a, b) => a + b, 0)
    const min = sorted[0]
    const max = sorted[sorted.length - 1]
    const avg = sum / count

    const quantileMap = new Map<number, number>()
    for (const q of this.quantiles) {
      const idx = Math.floor(q * (count - 1))
      quantileMap.set(q, sorted[idx])
    }

    return { count, sum, min, max, avg, quantiles: quantileMap }
  }

  getPointCount(): number {
    let total = 0
    for (const buffer of this.values.values()) {
      total += buffer.size()
    }
    return total
  }

  reset(): void {
    this.values.clear()
  }

  private labelsToKey(labels?: Record<string, string>): string {
    if (!labels || Object.keys(labels).length === 0) return '__default__'
    return Object.entries(labels)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join(',')
  }
}

// ============================================================================
// EXPORTER CLASSES
// ============================================================================

/**
 * Base exporter
 */
abstract class MetricExporter {
  protected config: ExportConfig
  protected stats: ExportStats

  constructor(config: ExportConfig) {
    this.config = config
    this.stats = {
      format: config.format,
      endpoint: config.endpoint,
      successCount: 0,
      errorCount: 0
    }
  }

  abstract export(metrics: BaseMetric[], labels: Record<string, string>): Promise<void>

  getStats(): ExportStats {
    return { ...this.stats }
  }
}

/**
 * Prometheus exporter
 */
class PrometheusExporter extends MetricExporter {
  async export(metrics: BaseMetric[], defaultLabels: Record<string, string>): Promise<void> {
    const lines: string[] = []

    for (const metric of metrics) {
      const meta = metric.getMetadata()
      const name = this.formatName(meta.name)

      // Add HELP and TYPE
      lines.push(`# HELP ${name} ${meta.description || meta.name}`)
      lines.push(`# TYPE ${name} ${this.mapType(meta.type)}`)

      // Add metric values
      if (metric instanceof Counter || metric instanceof Gauge) {
        const values = (metric as Counter | Gauge).getAllValues()
        for (const [labelKey, value] of values) {
          const labels = this.formatLabels(labelKey, defaultLabels)
          lines.push(`${name}${labels} ${value}`)
        }
      } else if (metric instanceof Histogram) {
        const data = (metric as Histogram).getData()
        const labels = this.formatLabels('__default__', defaultLabels)
        for (const bucket of data.buckets) {
          lines.push(`${name}_bucket{le="${bucket.le}"${labels ? ',' + labels.slice(1, -1) : ''}} ${bucket.count}`)
        }
        lines.push(`${name}_bucket{le="+Inf"${labels ? ',' + labels.slice(1, -1) : ''}} ${data.count}`)
        lines.push(`${name}_sum${labels} ${data.sum}`)
        lines.push(`${name}_count${labels} ${data.count}`)
      } else if (metric instanceof Summary) {
        const data = (metric as Summary).getData()
        const labels = this.formatLabels('__default__', defaultLabels)
        for (const [q, v] of data.quantiles) {
          lines.push(`${name}{quantile="${q}"${labels ? ',' + labels.slice(1, -1) : ''}} ${v}`)
        }
        lines.push(`${name}_sum${labels} ${data.sum}`)
        lines.push(`${name}_count${labels} ${data.count}`)
      }
    }

    const output = lines.join('\n')

    if (this.config.endpoint) {
      try {
        await fetch(this.config.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            ...this.config.headers
          },
          body: output
        })
        this.stats.successCount++
        this.stats.lastExport = Date.now()
      } catch (error: any) {
        this.stats.errorCount++
        this.stats.lastError = error.message
        throw error
      }
    }
  }

  private formatName(name: string): string {
    const prefix = this.config.prefix || ''
    return (prefix + name).replace(/[^a-zA-Z0-9_:]/g, '_')
  }

  private mapType(type: MetricType): string {
    switch (type) {
      case MetricType.COUNTER: return 'counter'
      case MetricType.GAUGE: return 'gauge'
      case MetricType.HISTOGRAM: return 'histogram'
      case MetricType.SUMMARY: return 'summary'
      default: return 'untyped'
    }
  }

  private formatLabels(labelKey: string, defaultLabels: Record<string, string>): string {
    const labels: Record<string, string> = { ...defaultLabels }

    if (labelKey !== '__default__') {
      const pairs = labelKey.split(',')
      for (const pair of pairs) {
        const [k, v] = pair.split('=')
        if (k && v) labels[k] = v
      }
    }

    if (Object.keys(labels).length === 0) return ''

    const labelStr = Object.entries(labels)
      .map(([k, v]) => `${k}="${v}"`)
      .join(',')

    return `{${labelStr}}`
  }
}

/**
 * JSON exporter
 */
class JSONExporter extends MetricExporter {
  async export(metrics: BaseMetric[], defaultLabels: Record<string, string>): Promise<void> {
    const output: any[] = []

    for (const metric of metrics) {
      const meta = metric.getMetadata()

      if (metric instanceof Counter || metric instanceof Gauge) {
        const values = (metric as Counter | Gauge).getAllValues()
        for (const [labelKey, value] of values) {
          output.push({
            name: meta.name,
            type: meta.type,
            value,
            labels: this.parseLabels(labelKey, defaultLabels),
            timestamp: Date.now()
          })
        }
      } else if (metric instanceof Histogram) {
        const data = (metric as Histogram).getData()
        output.push({
          name: meta.name,
          type: meta.type,
          data,
          labels: defaultLabels,
          timestamp: Date.now()
        })
      } else if (metric instanceof Summary) {
        const data = (metric as Summary).getData()
        output.push({
          name: meta.name,
          type: meta.type,
          data: {
            ...data,
            quantiles: Object.fromEntries(data.quantiles)
          },
          labels: defaultLabels,
          timestamp: Date.now()
        })
      }
    }

    if (this.config.endpoint) {
      try {
        await fetch(this.config.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...this.config.headers
          },
          body: JSON.stringify({ metrics: output })
        })
        this.stats.successCount++
        this.stats.lastExport = Date.now()
      } catch (error: any) {
        this.stats.errorCount++
        this.stats.lastError = error.message
        throw error
      }
    }
  }

  private parseLabels(labelKey: string, defaultLabels: Record<string, string>): Record<string, string> {
    const labels: Record<string, string> = { ...defaultLabels }

    if (labelKey !== '__default__') {
      const pairs = labelKey.split(',')
      for (const pair of pairs) {
        const [k, v] = pair.split('=')
        if (k && v) labels[k] = v
      }
    }

    return labels
  }
}

/**
 * StatsD exporter
 */
class StatsDExporter extends MetricExporter {
  async export(metrics: BaseMetric[], defaultLabels: Record<string, string>): Promise<void> {
    const lines: string[] = []
    const prefix = this.config.prefix || ''

    for (const metric of metrics) {
      const meta = metric.getMetadata()
      const name = prefix + meta.name.replace(/[^a-zA-Z0-9_.]/g, '_')

      if (metric instanceof Counter) {
        const values = (metric as Counter).getAllValues()
        for (const [labelKey, value] of values) {
          const tags = this.formatTags(labelKey, defaultLabels)
          lines.push(`${name}:${value}|c${tags}`)
        }
      } else if (metric instanceof Gauge) {
        const values = (metric as Gauge).getAllValues()
        for (const [labelKey, value] of values) {
          const tags = this.formatTags(labelKey, defaultLabels)
          lines.push(`${name}:${value}|g${tags}`)
        }
      } else if (metric instanceof Histogram || metric instanceof Summary) {
        const avg = metric.getValue()
        const tags = this.formatTags('__default__', defaultLabels)
        lines.push(`${name}:${avg}|ms${tags}`)
      }
    }

    if (this.config.endpoint && lines.length > 0) {
      try {
        await fetch(this.config.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'text/plain',
            ...this.config.headers
          },
          body: lines.join('\n')
        })
        this.stats.successCount++
        this.stats.lastExport = Date.now()
      } catch (error: any) {
        this.stats.errorCount++
        this.stats.lastError = error.message
        throw error
      }
    }
  }

  private formatTags(labelKey: string, defaultLabels: Record<string, string>): string {
    const labels: Record<string, string> = { ...defaultLabels }

    if (labelKey !== '__default__') {
      const pairs = labelKey.split(',')
      for (const pair of pairs) {
        const [k, v] = pair.split('=')
        if (k && v) labels[k] = v
      }
    }

    if (Object.keys(labels).length === 0) return ''

    const tagStr = Object.entries(labels)
      .map(([k, v]) => `${k}:${v}`)
      .join(',')

    return `|#${tagStr}`
  }
}

// ============================================================================
// ALERT MANAGER
// ============================================================================

class AlertManager {
  private alerts: Map<string, MetricAlert> = new Map()
  private alertListeners: Set<(event: AlertEvent) => void> = new Set()

  addAlert(alert: MetricAlert): void {
    this.alerts.set(alert.id, alert)
  }

  removeAlert(id: string): void {
    this.alerts.delete(id)
  }

  getAlerts(): MetricAlert[] {
    return Array.from(this.alerts.values())
  }

  onAlert(listener: (event: AlertEvent) => void): () => void {
    this.alertListeners.add(listener)
    return () => this.alertListeners.delete(listener)
  }

  checkAlerts(metrics: Map<string, BaseMetric>): AlertEvent[] {
    const events: AlertEvent[] = []
    const now = Date.now()

    for (const alert of this.alerts.values()) {
      if (!alert.enabled) continue

      // Check cooldown
      if (alert.lastTriggered && now - alert.lastTriggered < alert.cooldownMs) {
        continue
      }

      const metric = metrics.get(alert.metric)
      if (!metric) continue

      const value = this.getAggregatedValue(metric, alert.condition)
      const triggered = this.checkCondition(value, alert.condition)

      if (triggered) {
        alert.lastTriggered = now
        alert.triggerCount++

        const event: AlertEvent = {
          alertId: alert.id,
          alertName: alert.name,
          severity: alert.severity,
          metric: alert.metric,
          value,
          threshold: alert.condition.threshold,
          operator: alert.condition.operator,
          timestamp: now,
          labels: alert.labels
        }

        events.push(event)
        this.alertListeners.forEach(listener => listener(event))
      }
    }

    return events
  }

  private getAggregatedValue(metric: BaseMetric, condition: AlertCondition): number {
    if (metric instanceof Histogram) {
      switch (condition.aggregation) {
        case AggregationType.P50: return metric.getPercentile(0.5)
        case AggregationType.P75: return metric.getPercentile(0.75)
        case AggregationType.P90: return metric.getPercentile(0.9)
        case AggregationType.P95: return metric.getPercentile(0.95)
        case AggregationType.P99: return metric.getPercentile(0.99)
        default: return metric.getValue()
      }
    }
    return metric.getValue()
  }

  private checkCondition(value: number, condition: AlertCondition): boolean {
    switch (condition.operator) {
      case AlertOperator.GREATER_THAN: return value > condition.threshold
      case AlertOperator.LESS_THAN: return value < condition.threshold
      case AlertOperator.EQUAL: return value === condition.threshold
      case AlertOperator.NOT_EQUAL: return value !== condition.threshold
      case AlertOperator.GREATER_OR_EQUAL: return value >= condition.threshold
      case AlertOperator.LESS_OR_EQUAL: return value <= condition.threshold
      default: return false
    }
  }

  getStats(): { total: number; enabled: number; triggered: number } {
    let enabled = 0
    let triggered = 0

    for (const alert of this.alerts.values()) {
      if (alert.enabled) enabled++
      if (alert.triggerCount > 0) triggered++
    }

    return {
      total: this.alerts.size,
      enabled,
      triggered
    }
  }
}

// ============================================================================
// MAIN METRICS SERVICE
// ============================================================================

export class MetricsService {
  private static instance: MetricsService
  private config: MetricsConfig
  private metrics: Map<string, BaseMetric> = new Map()
  private exporters: MetricExporter[] = []
  private alertManager: AlertManager
  private listeners: Set<MetricEventListener> = new Set()
  private flushTimer: NodeJS.Timeout | null = null
  private alertTimer: NodeJS.Timeout | null = null
  private startTime: number

  private constructor(config: Partial<MetricsConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.alertManager = new AlertManager()
    this.startTime = Date.now()

    // Initialize exporters
    for (const exportConfig of this.config.exports) {
      this.addExporter(exportConfig)
    }

    // Start flush timer (server-side only)
    if (typeof window === 'undefined' && this.config.enabled) {
      this.flushTimer = setInterval(
        () => this.flush(),
        this.config.flushIntervalMs
      )

      this.alertTimer = setInterval(
        () => this.checkAlerts(),
        this.config.alertCheckIntervalMs
      )
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance(config?: Partial<MetricsConfig>): MetricsService {
    if (!MetricsService.instance) {
      MetricsService.instance = new MetricsService(config)
    }
    return MetricsService.instance
  }

  // ==========================================================================
  // METRIC CREATION
  // ==========================================================================

  /**
   * Create or get counter
   */
  counter(
    name: string,
    description?: string,
    unit?: MetricUnit,
    labelNames?: string[]
  ): Counter {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, new Counter(name, description, unit, labelNames))
    }
    return this.metrics.get(name) as Counter
  }

  /**
   * Create or get gauge
   */
  gauge(
    name: string,
    description?: string,
    unit?: MetricUnit,
    labelNames?: string[]
  ): Gauge {
    if (!this.metrics.has(name)) {
      this.metrics.set(name, new Gauge(name, description, unit, labelNames))
    }
    return this.metrics.get(name) as Gauge
  }

  /**
   * Create or get histogram
   */
  histogram(
    name: string,
    description?: string,
    unit?: MetricUnit,
    labelNames?: string[],
    buckets?: number[]
  ): Histogram {
    if (!this.metrics.has(name)) {
      this.metrics.set(
        name,
        new Histogram(name, description, unit, labelNames, buckets || this.config.defaultBuckets)
      )
    }
    return this.metrics.get(name) as Histogram
  }

  /**
   * Create or get summary
   */
  summary(
    name: string,
    description?: string,
    unit?: MetricUnit,
    labelNames?: string[],
    quantiles?: number[]
  ): Summary {
    if (!this.metrics.has(name)) {
      this.metrics.set(
        name,
        new Summary(name, description, unit, labelNames, quantiles || this.config.defaultQuantiles)
      )
    }
    return this.metrics.get(name) as Summary
  }

  // ==========================================================================
  // CONVENIENCE METHODS
  // ==========================================================================

  /**
   * Increment a counter
   */
  increment(name: string, value: number = 1, labels?: Record<string, string>): void {
    const counter = this.counter(name)
    counter.inc(labels, value)
    this.emitEvent({ type: 'record', metric: name, value, labels, timestamp: Date.now() })
  }

  /**
   * Record timing
   */
  timing(name: string, duration: number, labels?: Record<string, string>): void {
    const histogram = this.histogram(name, '', MetricUnit.MILLISECONDS)
    histogram.observe(duration, labels)
    this.emitEvent({ type: 'record', metric: name, value: duration, labels, timestamp: Date.now() })
  }

  /**
   * Set gauge value
   */
  setGauge(name: string, value: number, labels?: Record<string, string>): void {
    const gauge = this.gauge(name)
    gauge.set(value, labels)
    this.emitEvent({ type: 'record', metric: name, value, labels, timestamp: Date.now() })
  }

  /**
   * Measure async function execution
   */
  async measure<T>(
    name: string,
    fn: () => Promise<T>,
    labels?: Record<string, string>
  ): Promise<TimerResult<T>> {
    const start = Date.now()
    let success = true

    try {
      const result = await fn()
      return { result, duration: Date.now() - start, success: true }
    } catch (error) {
      success = false
      throw error
    } finally {
      const duration = Date.now() - start
      this.timing(name, duration, { ...labels, status: success ? 'success' : 'error' })
    }
  }

  /**
   * Create a timer that can be stopped manually
   */
  startTimer(name: string, labels?: Record<string, string>): { stop: () => number } {
    const start = Date.now()
    return {
      stop: () => {
        const duration = Date.now() - start
        this.timing(name, duration, labels)
        return duration
      }
    }
  }

  // ==========================================================================
  // API TRACKING
  // ==========================================================================

  /**
   * Track API call
   */
  trackAPICall(endpoint: string, method: string): {
    success: (statusCode?: number) => void
    error: (statusCode: number, errorType?: string) => void
  } {
    const start = Date.now()
    const baseLabels = { endpoint, method }

    return {
      success: (statusCode = 200) => {
        const duration = Date.now() - start
        this.timing('api.request.duration', duration, { ...baseLabels, status: 'success', statusCode: String(statusCode) })
        this.increment('api.request.total', 1, { ...baseLabels, status: 'success' })
      },
      error: (statusCode: number, errorType?: string) => {
        const duration = Date.now() - start
        this.timing('api.request.duration', duration, {
          ...baseLabels,
          status: 'error',
          statusCode: String(statusCode),
          ...(errorType && { errorType })
        })
        this.increment('api.request.total', 1, { ...baseLabels, status: 'error' })
        this.increment('api.request.errors', 1, { ...baseLabels, statusCode: String(statusCode) })
      }
    }
  }

  /**
   * Track database query
   */
  trackDBQuery(table: string, operation: string): {
    success: (rowCount?: number) => void
    error: (errorType?: string) => void
  } {
    const start = Date.now()
    const baseLabels = { table, operation }

    return {
      success: (rowCount = 0) => {
        const duration = Date.now() - start
        this.timing('db.query.duration', duration, { ...baseLabels, status: 'success' })
        this.increment('db.query.total', 1, { ...baseLabels, status: 'success' })
        if (rowCount > 0) {
          this.histogram('db.query.rows').observe(rowCount, baseLabels)
        }
      },
      error: (errorType = 'unknown') => {
        const duration = Date.now() - start
        this.timing('db.query.duration', duration, { ...baseLabels, status: 'error', errorType })
        this.increment('db.query.total', 1, { ...baseLabels, status: 'error' })
        this.increment('db.query.errors', 1, { ...baseLabels, errorType })
      }
    }
  }

  /**
   * Track LLM call
   */
  trackLLMCall(model: string, provider: string): {
    success: (tokens: { input: number; output: number }, latencyMs: number) => void
    error: (errorType?: string) => void
  } {
    const start = Date.now()
    const baseLabels = { model, provider }

    return {
      success: (tokens, latencyMs) => {
        this.timing('llm.request.duration', latencyMs, { ...baseLabels, status: 'success' })
        this.increment('llm.request.total', 1, { ...baseLabels, status: 'success' })
        this.increment('llm.tokens.input', tokens.input, baseLabels)
        this.increment('llm.tokens.output', tokens.output, baseLabels)
        this.increment('llm.tokens.total', tokens.input + tokens.output, baseLabels)
      },
      error: (errorType = 'unknown') => {
        const duration = Date.now() - start
        this.timing('llm.request.duration', duration, { ...baseLabels, status: 'error', errorType })
        this.increment('llm.request.total', 1, { ...baseLabels, status: 'error' })
        this.increment('llm.request.errors', 1, { ...baseLabels, errorType })
      }
    }
  }

  /**
   * Track cache operation
   */
  trackCache(cacheName: string): {
    hit: () => void
    miss: () => void
    set: (size?: number) => void
    delete: () => void
  } {
    const baseLabels = { cache: cacheName }

    return {
      hit: () => {
        this.increment('cache.hits', 1, baseLabels)
        this.increment('cache.requests', 1, baseLabels)
      },
      miss: () => {
        this.increment('cache.misses', 1, baseLabels)
        this.increment('cache.requests', 1, baseLabels)
      },
      set: (size?: number) => {
        this.increment('cache.sets', 1, baseLabels)
        if (size) {
          this.histogram('cache.entry.size').observe(size, baseLabels)
        }
      },
      delete: () => {
        this.increment('cache.deletes', 1, baseLabels)
      }
    }
  }

  // ==========================================================================
  // QUERYING
  // ==========================================================================

  /**
   * Query metrics
   */
  query(query: MetricQuery): QueryResult {
    const start = Date.now()
    const results: AggregatedMetric[] = []

    for (const [name, metric] of this.metrics) {
      // Check name match
      if (query.name instanceof RegExp) {
        if (!query.name.test(name)) continue
      } else if (query.name !== name) {
        continue
      }

      const meta = metric.getMetadata()
      const value = metric.getValue(query.labels)

      results.push({
        name,
        type: meta.type,
        value,
        aggregation: query.aggregation || AggregationType.LAST,
        labels: query.labels || {},
        timestamp: Date.now()
      })

      if (query.limit && results.length >= query.limit) break
    }

    return {
      metrics: results,
      total: results.length,
      executionTime: Date.now() - start
    }
  }

  /**
   * Get all metric names
   */
  getMetricNames(): string[] {
    return Array.from(this.metrics.keys())
  }

  /**
   * Get metric by name
   */
  getMetric(name: string): BaseMetric | undefined {
    return this.metrics.get(name)
  }

  // ==========================================================================
  // ALERTS
  // ==========================================================================

  /**
   * Add alert
   */
  addAlert(alert: Omit<MetricAlert, 'lastTriggered' | 'triggerCount'>): void {
    this.alertManager.addAlert({
      ...alert,
      triggerCount: 0
    })
  }

  /**
   * Remove alert
   */
  removeAlert(id: string): void {
    this.alertManager.removeAlert(id)
  }

  /**
   * Get alerts
   */
  getAlerts(): MetricAlert[] {
    return this.alertManager.getAlerts()
  }

  /**
   * Subscribe to alert events
   */
  onAlert(listener: (event: AlertEvent) => void): () => void {
    return this.alertManager.onAlert(listener)
  }

  /**
   * Check alerts manually
   */
  checkAlerts(): AlertEvent[] {
    return this.alertManager.checkAlerts(this.metrics)
  }

  // ==========================================================================
  // EXPORT
  // ==========================================================================

  /**
   * Add exporter
   */
  addExporter(config: ExportConfig): void {
    let exporter: MetricExporter

    switch (config.format) {
      case ExportFormat.PROMETHEUS:
        exporter = new PrometheusExporter(config)
        break
      case ExportFormat.STATSD:
        exporter = new StatsDExporter(config)
        break
      case ExportFormat.JSON:
      default:
        exporter = new JSONExporter(config)
        break
    }

    this.exporters.push(exporter)
  }

  /**
   * Flush metrics to all exporters
   */
  async flush(): Promise<void> {
    if (this.metrics.size === 0) return

    const metricsArray = Array.from(this.metrics.values())

    for (const exporter of this.exporters) {
      try {
        await exporter.export(metricsArray, this.config.defaultLabels)
      } catch (error: any) {
        console.error('Failed to export metrics:', error.message)
        this.emitEvent({
          type: 'error',
          error: error.message,
          timestamp: Date.now()
        })
      }
    }

    this.emitEvent({ type: 'flush', timestamp: Date.now() })
  }

  /**
   * Get Prometheus format output
   */
  getPrometheusOutput(): string {
    const exporter = new PrometheusExporter({
      format: ExportFormat.PROMETHEUS
    })
    const lines: string[] = []

    // Manually build output without sending
    for (const metric of this.metrics.values()) {
      const meta = metric.getMetadata()
      const name = meta.name.replace(/[^a-zA-Z0-9_:]/g, '_')

      lines.push(`# HELP ${name} ${meta.description || meta.name}`)
      lines.push(`# TYPE ${name} ${meta.type === MetricType.COUNTER ? 'counter' : meta.type === MetricType.GAUGE ? 'gauge' : meta.type === MetricType.HISTOGRAM ? 'histogram' : 'summary'}`)

      if (metric instanceof Counter || metric instanceof Gauge) {
        const values = (metric as Counter | Gauge).getAllValues()
        for (const [labelKey, value] of values) {
          const labels = this.formatPrometheusLabels(labelKey)
          lines.push(`${name}${labels} ${value}`)
        }
      }
    }

    return lines.join('\n')
  }

  private formatPrometheusLabels(labelKey: string): string {
    if (labelKey === '__default__') return ''

    const pairs = labelKey.split(',')
    const labels: string[] = []

    for (const pair of pairs) {
      const [k, v] = pair.split('=')
      if (k && v) labels.push(`${k}="${v}"`)
    }

    return labels.length > 0 ? `{${labels.join(',')}}` : ''
  }

  // ==========================================================================
  // STATISTICS
  // ==========================================================================

  /**
   * Get service stats
   */
  getStats(): MetricsStats {
    const metricsPerType: Record<MetricType, number> = {
      [MetricType.COUNTER]: 0,
      [MetricType.GAUGE]: 0,
      [MetricType.HISTOGRAM]: 0,
      [MetricType.SUMMARY]: 0,
      [MetricType.TIMER]: 0
    }

    let totalPoints = 0

    for (const metric of this.metrics.values()) {
      metricsPerType[metric.getType()]++
      totalPoints += metric.getPointCount()
    }

    return {
      totalMetrics: this.metrics.size,
      totalPoints,
      metricsPerType,
      exportStats: this.exporters.map(e => e.getStats()),
      alertStats: this.alertManager.getStats(),
      memoryUsageBytes: this.estimateMemoryUsage(),
      uptime: Date.now() - this.startTime
    }
  }

  private estimateMemoryUsage(): number {
    // Rough estimate: 100 bytes per metric + 20 bytes per point
    let total = this.metrics.size * 100
    for (const metric of this.metrics.values()) {
      total += metric.getPointCount() * 20
    }
    return total
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  /**
   * Add event listener
   */
  addEventListener(listener: MetricEventListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Reset all metrics
   */
  reset(): void {
    for (const metric of this.metrics.values()) {
      metric.reset()
    }
  }

  /**
   * Clear all metrics
   */
  clear(): void {
    this.metrics.clear()
  }

  /**
   * Destroy service
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    if (this.alertTimer) {
      clearInterval(this.alertTimer)
      this.alertTimer = null
    }
    this.flush() // Final flush
  }

  private emitEvent(event: MetricEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        console.error('Metric event listener error:', error)
      }
    })
  }
}

// ============================================================================
// LEGACY EXPORTS (Backwards Compatibility)
// ============================================================================

/**
 * Legacy metrics collector instance
 */
class LegacyMetricsCollector {
  private service: MetricsService

  constructor() {
    this.service = MetricsService.getInstance()
  }

  increment(name: string, value: number = 1, tags?: Record<string, string>): void {
    this.service.increment(name, value, tags)
  }

  timing(name: string, duration: number, tags?: Record<string, string>): void {
    this.service.timing(name, duration, tags)
  }

  gauge(name: string, value: number, tags?: Record<string, string>): void {
    this.service.setGauge(name, value, tags)
  }

  async measure<T>(name: string, fn: () => Promise<T>, tags?: Record<string, string>): Promise<T> {
    const result = await this.service.measure(name, fn, tags)
    return result.result
  }

  destroy(): void {
    this.service.destroy()
  }
}

export const metrics = new LegacyMetricsCollector()

/**
 * Track API call (legacy)
 */
export function trackAPICall(endpoint: string, method: string): {
  success: () => void
  error: (statusCode: number) => void
} {
  const service = MetricsService.getInstance()
  const tracker = service.trackAPICall(endpoint, method)

  return {
    success: () => tracker.success(),
    error: (statusCode: number) => tracker.error(statusCode)
  }
}

/**
 * Get metrics service instance
 */
export function getMetricsService(config?: Partial<MetricsConfig>): MetricsService {
  return MetricsService.getInstance(config)
}
